import React, { useMemo, useContext, useState, useCallback } from "react";
import clone from "lodash/clone";
import { useMutation } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import classnames from "classnames";
import {
  withStyles,
  ServerNotificationsContext,
  BreakpointContext,
  MobileContext
} from "components/common";
import Content from "components/common/NotificationsBanners/common/Content";
import Overlay from "components/common/Overlay/Overlay";
import CrossIcon from "components/common/Svg/Cross";
import { convertToShortDate } from "utils/convert-date";
import Table from "components/common/Table";
import TableFilter from "components/common/TableFilter";
import { UPDATE_NOTIFICATION } from "utils/mutations";


const mapping = {};
const tableFilter = ({ showDescription = true, belowMd }) => (
  <div className="flex">
    {showDescription && <p className="">Description</p>}
    <TableFilter
      mapping={mapping}
      containerClassName={classnames(
        "flex flex-1 items-center text-gray-primary font-light",
        { "justify-end": !belowMd }
      )}
      initialActive="All"
    />
  </div>
);

const computeColumns = ({ isBelowSize, push, canExpand, deleteNotification }) =>
  isBelowSize
    ? [
        {
          Header: "Date",
          accessor: (data) => (
            <div className="whitespace-nowrap align-top">
              {convertToShortDate(data.created_at)}
            </div>
          ),
          style: { verticalAlign: "top" },
        },
        {
          Header: () => (
            <div className="flex">
              <p className="">Description</p>
            </div>
          ),
          accessor: "Description",
          // weird hack to make the cell have an ellipsis without hard coding the width
          // https://stackoverflow.com/questions/14826992/text-overflow-ellipsis-in-table-cell-without-width
          // there's prob an easier way
          Cell: (cell) => {
            const {
              row: {
                original: { title, subtitle, body, list, link, read, expanded },
              },
            } = cell;
            return (
              <table className="text-left tomo-table" style={{ tableLayout: "fixed" }}>
                <tr className="flex p-0">
                  <div className="truncate flex-1">
                    <div className={`truncate ${read ? "" : "font-bold"}`}>
                      {title}
                    </div>
                    <div className="whitespace-normal">
                      {canExpand && expanded && (
                        <Content
                          subtitle={subtitle}
                          list={list}
                          body={body}
                          link={link}
                          onClick={() => push(link.route)}
                          className="mt-4"
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end" style={{ width: "100px" }}>
                    <CrossIcon
                      hasCrossContainer={false}
                      color="gray"
                      onClick={deleteNotification}
                    />
                  </div>
                </tr>
              </table>
            );
          },
        },
      ]
    : [
        {
          Header: "Date",
          accessor: (data) => (
            <div className="whitespace-nowrap align-top">
              {convertToShortDate(data.created_at)}
            </div>
          ),
          style: { verticalAlign: "top" },
        },
        {
          Header: tableFilter,
          accessor: "Description",
          // weird hack to make the cell have an ellipsis without hard coding the width
          // https://stackoverflow.com/questions/14826992/text-overflow-ellipsis-in-table-cell-without-width
          // there's prob an easier way
          Cell: (cell) => {
            const {
              row,
              row: {
                original: {
                  title,
                  subtitle,
                  body,
                  list,
                  priority,
                  link,
                  read,
                  expanded,
                },
              },
            } = cell;
            return (
              <table className="text-left tomo-table" style={{ tableLayout: "fixed" }}>
                <tbody style={{ boxShadow: "none" }}>
                  <tr className="flex p-0">
                    <td className="truncate flex-1 p-0">
                      <div className={`truncate ${read ? "" : "font-bold"}`}>
                        {title}
                      </div>
                      <div className="whitespace-normal">
                        {canExpand && expanded && (
                          <Content
                            subtitle={subtitle}
                            list={list}
                            body={body}
                            link={link}
                            onClick={() => push(link.route)}
                            className="mt-4"
                          />
                        )}
                      </div>
                    </td>
                    <td className="ml-2 p-0" style={{ width: "90px" }}>
                      <div className="flex items-center justify-between">
                        <div
                          className={classnames("rounded-full w-3 h-3", {
                            "bg-red-400": priority === 1,

                            "bg-yellow-400": priority === 2,
                          })}
                        />
                        <div className="underline">
                          {canExpand && expanded ? "less" : "more"}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(row);
                          }}
                        >
                          <CrossIcon hasCrossContainer={false} color="gray" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            );
          },
        },
      ];

const NotificationsTable = () => {
  const { isBelow } = useContext(BreakpointContext);
  const { isMobile } = useContext(MobileContext);
  const { push } = useHistory();
  const belowMd = isBelow("md");
  const { getNotifications, getServerNotifications } = useContext(
    ServerNotificationsContext
  );
  const [rowInfo, setRowInfo] = useState();
  const [rowExpanded, setRowExpanded] = useState();

  const [updateNotification] = useMutation(UPDATE_NOTIFICATION);
  const [filteredNotifications, setFilteredNotifications] = useState();

  const notifications = filteredNotifications || getNotifications();

  const deleteNotification = useCallback(
    row => {
      console.log("here doing stuff");
      return updateNotification({
        variables: {
          id: row.original.uuid,
          payload: { read: true, deleted: true }
        }
      }).then(() => {
        getServerNotifications();
      });
    },
    [updateNotification, getServerNotifications]
  );


  const columns = useMemo(
    () =>
      computeColumns({
        isBelowSize: belowMd,
        push,
        canExpand: !isMobile,
        deleteNotification
      }),
    [belowMd, isMobile, deleteNotification, push]
  );

  mapping.all = {
    name: "All",
    fn: () => setFilteredNotifications(getNotifications())
  };
  mapping.critical = {
    fn: () => setFilteredNotifications(getNotifications({ priority: 1 })),
    Component: ({ elementClass }) => (
      <div className="flex px-2 items-center">
        <div className="rounded-full w-3 h-3 bg-red-400" />
        <div className={classnames("pl-2", elementClass)}>
          {`Critical (${getNotifications({ priority: 1 }).length})`}
        </div>
      </div>
    )
  };
  mapping.alert = {
    fn: () => setFilteredNotifications(getNotifications({ priority: 2 })),
    Component: ({ elementClass }) => (
      <div className="flex px-2 items-center">
        <div className="rounded-full w-3 h-3 bg-yellow-400" />
        <div className={classnames("pl-2", elementClass)}>
          {`Alert (${getNotifications({ priority: 2 }).length})`}
        </div>
      </div>
    )
  };
  // mapping.dismissAll = {
  //   name: "Dismiss All",
  //   fn: () =>
  //     setFilteredNotifications(
  //       notifications.filter(ref => ref.status === "expired")
  //     )
  // };

  const rowClicked = row => {
    setRowInfo(row);
    setRowExpanded(!rowExpanded);
    if (!row.original.read) {
      updateNotification({
        variables: {
          id: row.original.uuid,
          payload: { read: true }
        }
      }).then(() => {
        getServerNotifications();
      });
    }
  };

  return (
    <div>
      <div className="my-6">
        <>
          {belowMd && tableFilter({ showDescription: false, belowMd })}
          <Table
            id="notifications-table"
            data={clone(
              notifications.map(n => ({
                ...n,
                expanded:
                  rowExpanded && rowInfo && rowInfo.original.uuid === n.uuid
              }))
            )}
            columns={columns}
            onRowClick={(e, row) => rowClicked(row)}
          />
        </>
      </div>
      <Overlay isOpen={!!rowInfo && isMobile} close={() => setRowInfo(null)}>
        {rowInfo && (
          <div
            className="pt-6 pb-8 px-12 h-full"
            style={{ backgroundColor: "#F9F9F9" }}
          >
            <p className="mb-4">
              {convertToShortDate(rowInfo.original.created_at)}
            </p>
            <p className="font-bold mb-4">{rowInfo.original.title}</p>
            <Content
              subtitle={rowInfo.original.subtitle}
              list={rowInfo.original.list}
              body={rowInfo.original.body}
              link={rowInfo.original.link}
              onClick={() => push(rowInfo.original.link.route)}
              className="mt-4"
            />
            <div
              className="absolute w-full left-0 bottom-0 py-4 px-8 flex justify-between"
              style={{ backgroundColor: "#F1F6F7" }}
            >
              <button
                type="button"
                className="flex items-center btn py-1 font-medium"
                onClick={() => {}}
              >
                Dismiss
              </button>
              <button
                type="button"
                className="flex items-center"
                onClick={() => setRowInfo(null)}
              >
                <p className="pr-1">close</p>
                <p>x</p>
              </button>
            </div>
          </div>
        )}
      </Overlay>
    </div>
  );
};

export default withStyles(NotificationsTable);
